import { api } from '@/api/client';
import type { CustomerData, ProductData } from '@/types/generated';

/**
 * Customers and the service catalogue.
 *
 * The response types are GENERATED from the PHP DTOs, so the wire shape and this
 * client cannot drift — `make types` regenerates them and CI fails if the
 * checked-in file is stale.
 *
 * These endpoints are CURSOR-paginated, not offset-paginated, so there is no
 * total and no page number: an offset page silently skips or repeats rows under
 * concurrent inserts, and `OFFSET 100000` makes Postgres walk and discard 100,000
 * rows. The consequence is a "load more" list rather than numbered pages, which is
 * a deliberate trade rather than an omission.
 */
export interface CursorPage<T> {
  data: T[];
  next_cursor: string | null;
  per_page: number;
}

/**
 * `| undefined` is explicit on every optional field because the project runs with
 * `exactOptionalPropertyTypes`. Under that flag `search?: string` means "absent or
 * a string" and NOT "may be set to undefined" — so passing an unset filter through
 * fails to type-check, which is the correct strictness and needs saying once here
 * rather than being worked around at each call site.
 */
export interface CustomerFilters {
  search?: string | undefined;
  tier?: string | undefined;
  status?: string | undefined;
  cursor?: string | null | undefined;
  /** Server caps this at 100. Useful for a select that wants one page, not a list. */
  per_page?: number | undefined;
}

export async function fetchCustomers(filters: CustomerFilters = {}): Promise<CursorPage<CustomerData>> {
  const { data } = await api.get<CursorPage<CustomerData>>('/customers', {
    // Empty values are dropped rather than sent as `?search=`, which the API
    // would treat as a filter on the empty string.
    params: pruned({
      search: filters.search,
      tier: filters.tier,
      status: filters.status,
      cursor: filters.cursor ?? undefined,
      per_page: filters.per_page,
    }),
  });
  return data;
}

export async function fetchCustomer(id: string): Promise<CustomerData> {
  const { data } = await api.get<{ customer: CustomerData }>(`/customers/${id}`);
  return data.customer;
}

export interface ProductFilters {
  search?: string | undefined;
  per_page?: number | undefined;
  kind?: string | undefined;
  category?: string | undefined;
  activeOnly?: boolean | undefined;
  cursor?: string | null | undefined;
}

export async function fetchProducts(filters: ProductFilters = {}): Promise<CursorPage<ProductData>> {
  const { data } = await api.get<CursorPage<ProductData>>('/products', {
    params: pruned({
      search: filters.search,
      kind: filters.kind,
      category: filters.category,
      active_only: filters.activeOnly ? 1 : undefined,
      cursor: filters.cursor ?? undefined,
      per_page: filters.per_page,
    }),
  });
  return data;
}

function pruned(params: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== '' && value !== null),
  );
}

// --- Formatting --------------------------------------------------------------

/**
 * Money arrives as integer MINOR UNITS with its currency alongside — never a
 * float, and never pre-formatted, because the viewer's locale is the client's
 * business. Formatting is centralised here so no component divides by 100 on its
 * own and none of them disagrees about rounding.
 */
export function formatMinor(minor: number | null, currency: string): string {
  if (minor === null) {
    return '—';
  }

  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(minor / 100);
}

/**
 * Payment terms as an operator says them.
 */
export function formatTerms(days: number): string {
  return days === 0 ? 'Prepaid' : `Net ${days}`;
}
